// LivestreamUnmuteHint.jsx
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import trackMixpanel, { getViewId } from '../resource/mixpanel.js';
import { EventTypes, ModalId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import { breakpoint, color } from '../style/variables.js';

import AudioOffIconSrc from '../../img/ic-audio-off.svg';

const MOBILE_VIEWPORT_QUERY = `(max-width: ${breakpoint.tablet}px)`;

/**
 * Whether the footer that owns this copy of the mute button is the one CSS is
 * currently showing. The mobile and desktop footers are both mounted and only
 * hidden by CSS, and a hidden copy would still fire the tracking and mark the
 * hint seen, so the hint has to know which side it is on. Asks the browser
 * instead of the Redux device width, which is written by a debounced resize
 * handler and is still unknown on the first render.
 * @kind function
 * @param {boolean} [isForMobileLayout] - True for the mobile footer copy.
 * @return {boolean} True when this copy is the visible one.
 * @example
 * getIsUnmuteHintVisibleLayout({ isForMobileLayout: true });
 */
export const getIsUnmuteHintVisibleLayout = ({
  isForMobileLayout = false,
} = {}) => {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return isForMobileLayout === window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
};

/**
 * Send the exposure event for the unmute hint.
 * Call this from Tooltip's onHintOpened so it only fires when the hint is
 * actually shown -- Tooltip keeps renderHint mounted even while hidden.
 * @kind function
 * @return {void}
 */
export const trackUnmuteHintOpened = () =>
  trackMixpanel({
    type: EventTypes.VIEW_OPENED,
    payload: {
      viewId: getViewId({ pathname: window.location.pathname }),
      modalId: ModalId.LiveStream.UnmuteHint,
    },
  });

/**
 * Content of the one-off hint shown above the livestream mute button, telling
 * the viewer the stream is muted and tapping the button turns the sound on.
 * Positioning, timing and fading are handled by the Tooltip wrapper.
 * @returns {React.ReactNode} The hint content.
 */
export const LivestreamUnmuteHint = () => {
  const { t } = useTranslation();

  return (
    <StyledLivestreamUnmuteHint>
      <HintIcon width={32} height={32} src={AudioOffIconSrc} alt="" />
      <HintTitle>
        {t('toast_unmute_hint_title', {
          ns: TranslationNamespace.LIVESTREAM,
        })}
      </HintTitle>
      <HintSubtitle>
        {t('toast_unmute_hint_subtitle', {
          ns: TranslationNamespace.LIVESTREAM,
        })}
      </HintSubtitle>
    </StyledLivestreamUnmuteHint>
  );
};

LivestreamUnmuteHint.propTypes = {};

// Half the mute button width, so the arrow points at the button center no
// matter how wide the translated text makes the bubble.
const ARROW_OFFSET_PX = 20;
const ARROW_HEIGHT_PX = 6;

const StyledLivestreamUnmuteHint = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: max-content;
  max-width: 160px;
  margin-bottom: ${ARROW_HEIGHT_PX + 2}px;
  padding: 6px 8px;
  border-radius: 8px;
  background: ${color.neutral['10']};

  /* BottomLeft arrow, per the Figma tooltip component. */
  ::after {
    content: '';
    position: absolute;
    top: 100%;
    left: ${ARROW_OFFSET_PX}px;
    transform: translateX(-50%);
    border: ${ARROW_HEIGHT_PX}px solid transparent;
    border-bottom: 0;
    border-top-color: ${color.neutral['10']};
  }
`;

const HintIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const HintTitle = styled.div`
  margin-top: 2px;
  color: ${color.white};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
`;

const HintSubtitle = styled.div`
  color: ${color.white};
  font-size: 12px;
  font-weight: 400;
  line-height: 14px;
  text-align: center;
`;

export default LivestreamUnmuteHint;
